import { useEffect, useRef } from "react";
import "./App.css";
import { Toaster } from "react-hot-toast";
import PostHogService from "./posthog";
import { DarkModeProvider } from "./components/DarkModeContext";
import { ClerkThemeProvider } from "./components/ClerkThemeProvider";
import { useAuth, AuthProvider } from "./contexts/AuthContext";
import { OrganizationProvider, useOrganization } from "./contexts/OrganizationContext";
import { ApplicationProvider } from "./contexts/ApplicationContext";
import { ScanProgressProvider } from "./contexts/ScanProgressContext";
import { TourProvider } from "./components/tour";
import { HelpButton } from "./components/tour";
import { SettingsModalProvider } from "./contexts/SettingsModalContext";
import SettingsModals from "./components/modals/SettingsModals";
import PaymentRedirect from "./components/payment/PaymentRedirect";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { usePostAuthRedirect } from "./hooks/usePostAuthRedirect";
import { useDemoReturnRedirect } from "./hooks/useDemoReturnRedirect";
import { usePaddleInit } from "./hooks/usePaddleInit";
import SubscriptionRequiredRoute from "./components/auth/SubscriptionRequiredRoute";
import OrganizationRequiredRoute from "./components/auth/OrganizationRequiredRoute";
import RepositoryAppGuard from "./components/auth/RepositoryAppGuard";
import OrganizationSetupPage from "./pages/OrganizationSetupPage";
import InviteAcceptPage from "./pages/InviteAcceptPage";
import DeviceApprovalPage from "./pages/auth/DeviceApprovalPage";
import OAuthConsentPage from "./pages/auth/OAuthConsentPage";
import HomePage from "./pages/HomePage";
import SignUpPage from "./pages/SignUpPage";
import SubscriptionRequiredPage from "./pages/SubscriptionRequiredPage";
import Header from "./components/common/Header";
import ImpersonationBanner from "./components/admin/ImpersonationBanner";
import { IntegrationStatusProvider } from "./contexts/IntegrationStatusContext";
import { MobileNavProvider } from "./contexts/MobileNavContext";
import AdminPage from "./pages/AdminPage";
import SidebarLayout from "./components/layouts/SidebarLayout";
import AuthLayout from "./components/layouts/AuthLayout";
import RepositoriesIndexPage from "./pages/RepositoriesIndexPage";
import ApplicationDetailPage from "./pages/ApplicationDetailPage";
import SbomListPage from "./pages/sbom/SbomListPage";
import SbomDetailPage from "./pages/sbom/SbomDetailPage";
import LicensePolicyPage from "./pages/sbom/LicensePolicyPage";
import FixesBoardPage from "./pages/fixes/FixesBoardPage";
import FixDetailPage from "./pages/FixDetailPage";
import OnboardingGitPage from "./pages/OnboardingGitPage";
import OnboardingApplicationPage from "./pages/OnboardingApplicationPage";
import ScansListPage from "./pages/ScansListPage";
// Scan detail pages are hidden — progress is now shown on the scans list and the
// repository-wide progress banner. Components kept for potential future use.
// import ScanDetailPage from "./pages/ScanDetailPage";
// import ScanBatchDetailPage from "./pages/ScanBatchDetailPage";
import FindingsListPage from "./pages/FindingsListPage";
import FindingDetailPage from "./pages/FindingDetailPage";
import PublicScanProjectPage from "./pages/PublicScanProjectPage";
import PricingPage from "./pages/public/PricingPage";
import TermsPage from "./pages/public/TermsPage";
import PrivacyPage from "./pages/public/PrivacyPage";
import RefundPolicyPage from "./pages/public/RefundPolicyPage";
import ContactPage from "./pages/public/ContactPage";
import { ComplianceOverviewPage, ComplianceRequirementsPage, ComplianceWorkloadPage, SlaSettingsPage } from "./pages/compliance";
import CreateOrganizationPage from "./pages/organizations/CreateOrganizationPage";
import OrganizationSettingsPage from "./pages/organizations/OrganizationSettingsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import { isLocalAuth } from "./config/auth";
import { initSentry } from "./config/sentry";
import ChatwootWidget from "./components/ChatwootWidget";
import { getDemoPageViewEvent } from "./utils/demoAnalytics";


function AppContent() {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { currentOrganization } = useOrganization();
  const lastDemoPageViewKey = useRef<string | null>(null);

  // Return the user to a stashed URL after login (e.g. device-auth page).
  usePostAuthRedirect(isAuthenticated);
  useDemoReturnRedirect(isAuthenticated);
  // Initialize Paddle.js and auto-open checkout for Paddle-initiated links (?_ptxn=).
  usePaddleInit();

  // Track page view on route changes
  useEffect(() => {
    // Track page view
    PostHogService.trackPageView(location.pathname, {
      loggedIn: isAuthenticated,
      userId: user?.id,
      userEmail: user?.email,
      organization_id: currentOrganization?.id,
      organization_role: currentOrganization?.current_user_role,
      organization_is_demo: currentOrganization?.is_demo,
    });
  }, [
    location.pathname,
    isAuthenticated,
    user?.id,
    user?.email,
    currentOrganization?.id,
    currentOrganization?.current_user_role,
    currentOrganization?.is_demo,
  ]);

  useEffect(() => {
    if (!currentOrganization?.is_demo) {
      lastDemoPageViewKey.current = null;
      return;
    }

    const demoPageEvent = getDemoPageViewEvent(location.pathname, location.search);
    if (!demoPageEvent) {
      lastDemoPageViewKey.current = null;
      return;
    }

    const trackingKey = `${currentOrganization.id}:${location.pathname}${location.search}:${demoPageEvent.eventName}`;
    if (lastDemoPageViewKey.current === trackingKey) {
      return;
    }
    lastDemoPageViewKey.current = trackingKey;

    PostHogService.track(demoPageEvent.eventName, {
      ...demoPageEvent.properties,
      organization_id: currentOrganization.id,
    });
  }, [
    currentOrganization?.id,
    currentOrganization?.is_demo,
    location.pathname,
    location.search,
  ]);

  // Identify authenticated users to PostHog
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      PostHogService.identify(user.id, {
        email: user.email,
        name: user.name,
        current_organization_id: currentOrganization?.id,
        current_organization_role: currentOrganization?.current_user_role,
      });
    }
  }, [isAuthenticated, user?.id, user?.email, user?.name, currentOrganization?.id, currentOrganization?.current_user_role]);

  // Determine current page name for help system
  const getCurrentPageName = () => {
    const path = location.pathname;
    if (path === '/overview') return 'overview';
    if (path.startsWith('/admin')) return 'admin';
    if (path.startsWith('/account')) return 'account';
    return 'default';
  };

  // Show header for authenticated users (homepage, org setup, and onboarding have their own layouts)
  const showHeader = isAuthenticated &&
    location.pathname !== '/' &&
    location.pathname !== '/organization-setup' &&
    !location.pathname.startsWith('/onboarding/') &&
    !location.pathname.startsWith('/invite/') &&
    !location.pathname.startsWith('/public/') &&
    location.pathname !== '/auth/device' &&
    location.pathname !== '/auth/authorize';

  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath === '/') {
      localStorage.removeItem('projectPath');
      localStorage.removeItem('projectPort');
    }
  }, []);

  return (
    <>
      <div className="flex flex-col h-screen bg-ui-background-primary">
        {showHeader && <Header />}
        <ImpersonationBanner />
        <div className={`${showHeader ? 'flex-1 overflow-auto' : 'h-screen'}`}>
        <Routes>
          {/* Public routes - homepage for login/signup */}
          <Route element={<AuthLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/sign-up" element={<SignUpPage />} />
          </Route>

          {/* Local auth routes (password reset) */}
          {isLocalAuth && <Route path="/forgot-password" element={<ForgotPasswordPage />} />}
          {isLocalAuth && <Route path="/reset-password" element={<ResetPasswordPage />} />}

          {/* OIDC callback */}
          <Route path="/oidc/callback" element={<HomePage />} />

          {/* Invitation acceptance page - public route */}
          <Route path="/invite/:token" element={<InviteAcceptPage />} />

          {/* Public project security status page */}
          <Route path="/public/projects/:orgSlug/:projectSlug" element={<PublicScanProjectPage />} />

          {/* Public storefront (pricing + legal) — shown on dev to satisfy Paddle's website
              review, gated off in production via the env flag. When off, these paths fall
              through to the catch-all and redirect to "/". */}
          {process.env.REACT_APP_SHOW_PUBLIC_PAGES === 'true' && (
            <>
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/refund" element={<RefundPolicyPage />} />
              <Route path="/contact" element={<ContactPage />} />
            </>
          )}

          {/* Legacy login route - redirect to homepage */}
          <Route path="/login" element={<Navigate to="/" replace />} />

          {/* Organization setup page - needs auth but not organization */}
          <Route path="/organization-setup" element={
            <ProtectedRoute>
              <OrganizationSetupPage />
            </ProtectedRoute>
          } />

          {/* CLI device pairing approval - needs auth but lets the user pick the org inline */}
          <Route path="/auth/device" element={
            <ProtectedRoute>
              <DeviceApprovalPage />
            </ProtectedRoute>
          } />

          {/* OAuth consent for remote MCP clients (any MCP client using OAuth) */}
          <Route path="/auth/authorize" element={
            <ProtectedRoute>
              <OAuthConsentPage />
            </ProtectedRoute>
          } />

          {/* Onboarding flow pages - needs auth but not subscription */}
          <Route path="/onboarding/connect" element={
            <ProtectedRoute>
              <OnboardingGitPage />
            </ProtectedRoute>
          } />
          <Route path="/onboarding/application" element={
            <ProtectedRoute>
              <OnboardingApplicationPage />
            </ProtectedRoute>
          } />

          {/* Subscription required page - needs auth but not subscription */}
          <Route path="/subscription-required" element={
            <ProtectedRoute>
              <SubscriptionRequiredPage />
            </ProtectedRoute>
          } />


          {/* Admin routes - require auth but NOT subscription (handled in SubscriptionRequiredRoute) */}
          <Route path="/admin/*" element={
            <SubscriptionRequiredRoute>
              <AdminPage />
            </SubscriptionRequiredRoute>
          } />

          {/* All sidebar routes share the SidebarLayout */}
          <Route
            element={
              <OrganizationRequiredRoute>
                <SubscriptionRequiredRoute>
                  <SidebarLayout />
                </SubscriptionRequiredRoute>
              </OrganizationRequiredRoute>
            }
          >
            {/* Applications section */}
            <Route path="/repositories" element={<RepositoriesIndexPage />} />
            {/* All application-scoped routes are guarded so a URL whose application
                id belongs to a different organization than the active workspace
                (e.g. after a workspace switch or the browser back button) heals to
                the /repositories resolver instead of dead-ending on the page. */}
            <Route element={<RepositoryAppGuard />}>
              <Route path="/repositories/:applicationId" element={<ApplicationDetailPage />} />
              <Route path="/repositories/:applicationId/settings/source" element={<ApplicationDetailPage />} />
              <Route path="/repositories/:applicationId/settings/security-context" element={<ApplicationDetailPage />} />
              <Route path="/repositories/:applicationId/settings/schedules" element={<ApplicationDetailPage />} />
              <Route path="/repositories/:applicationId/settings/public-status" element={<ApplicationDetailPage />} />
              <Route path="/repositories/:applicationId/scans" element={<ScansListPage />} />
              {/* Scan batch + individual scan detail pages are hidden; progress is shown
                  on the scans list and the repository-wide progress banner. */}
              {/* <Route path="/repositories/:applicationId/scans/:batchId" element={<ScanBatchDetailPage />} /> */}
              {/* <Route path="/repositories/:applicationId/scans/:batchId/:scanId" element={<ScanDetailPage />} /> */}
              <Route path="/repositories/:applicationId/findings" element={<FindingsListPage />} />
              <Route path="/repositories/:applicationId/findings/:findingId" element={<FindingDetailPage />} />
              <Route path="/repositories/:applicationId/sbom" element={<SbomListPage />} />
              <Route path="/repositories/:applicationId/sbom/:sbomId" element={<SbomDetailPage />} />
              {/* Fixes board for the repository; a fix opens on its own detail page. */}
              <Route path="/repositories/:applicationId/fixes" element={<FixesBoardPage />} />
              <Route path="/repositories/:applicationId/fixes/:fixId" element={<FixDetailPage />} />
            </Route>
            <Route path="/settings/license-policy" element={<LicensePolicyPage />} />

            {/* Compliance section */}
            <Route path="/compliance" element={<ComplianceOverviewPage />} />
            <Route path="/compliance/requirements" element={<ComplianceRequirementsPage />} />
            <Route path="/compliance/requirements/:frameworkId" element={<ComplianceRequirementsPage />} />
            <Route path="/compliance/sla-settings" element={<SlaSettingsPage />} />
            {process.env.REACT_APP_SHOW_WORKLOAD_MOCKUP === 'true' && (
              <Route path="/compliance/workload" element={<ComplianceWorkloadPage />} />
            )}

            {/* Queue page retired — redirect to repositories (component kept in repo) */}
            <Route path="/queue" element={<Navigate to="/repositories" replace />} />
            <Route path="/queue/resolved" element={<Navigate to="/repositories" replace />} />

            {/* Organization management */}
            <Route path="/organizations/new" element={<CreateOrganizationPage />} />
            <Route path="/organizations/:orgId/settings" element={<OrganizationSettingsPage />} />
            <Route path="/organizations/:orgId/settings/:section" element={<OrganizationSettingsPage />} />

            {/* Legacy routes - redirect to new structure */}
            <Route path="/overview" element={<Navigate to="/repositories" replace />} />
            <Route path="/scans" element={<Navigate to="/repositories" replace />} />
            <Route path="/findings" element={<Navigate to="/repositories" replace />} />
            <Route path="/fixes" element={<Navigate to="/repositories" replace />} />
            <Route path="/workload" element={<Navigate to="/repositories" replace />} />
          </Route>

          {/* Redirect legacy project routes to applications */}
          <Route path="/select-project" element={<Navigate to="/repositories" replace />} />
          <Route path="/deleted-projects" element={<Navigate to="/repositories" replace />} />
          <Route path="/project/*" element={<Navigate to="/repositories" replace />} />

          {/* Payment redirect route */}
          <Route path="/payments/:encodedData" element={<PaymentRedirect />} />

          {/* Catch-all route - redirect unknown paths to homepage */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </div>
        {/* Global HelpButton - visible on all authenticated pages */}
        {isAuthenticated && !location.pathname.startsWith('/public/') && <HelpButton pageName={getCurrentPageName()} />}
        
        {/* Settings Modals - accessible from anywhere */}
        <SettingsModals />

        {/* Chatwoot live chat widget */}
        <ChatwootWidget />
      </div>
    </>
  );
}


function App() {
  useEffect(() => {
    // Initialize Sentry for error tracking and user feedback
    initSentry();
  }, []);

  return (
    <Router>
      <DarkModeProvider>
        <ClerkThemeProvider>
          <AuthProvider>
            <OrganizationProvider>
              <IntegrationStatusProvider>
                <SettingsModalProvider>
                  <TourProvider>
                    <ApplicationProvider>
                      <ScanProgressProvider>
                        <MobileNavProvider>
                          <AppContent />
                        </MobileNavProvider>
                      </ScanProgressProvider>
                    </ApplicationProvider>
                    {/* Toasts render the on-brand design-system <Toast> via
                        `design-system/toast`. Styling/duration/a11y live there;
                        the Toaster only positions the stack. */}
                    <Toaster
                      position="bottom-left"
                      reverseOrder={false}
                      gutter={8}
                      containerStyle={{
                        bottom: 16,
                        left: 16,
                      }}
                    />
                  </TourProvider>
                </SettingsModalProvider>
              </IntegrationStatusProvider>
            </OrganizationProvider>
          </AuthProvider>
        </ClerkThemeProvider>
      </DarkModeProvider>
    </Router>
  );
}

export default App;
